import { useMemo, useState } from "react";
import { NextRouter } from "next/router";

import { Button, LoadingSpinner, ChevronUpSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import AttachmentPanel from "@/bundles/components/AttachmentPanel";
import Footer from "@/base/Footer";
import MainContent from "@/bundles/components/MainContent/MainContent";
import Breadcrumbs from "@/bundles/components/Breadcrumbs";
import GeneaologyPanel from "@/bundles/components/GeneaologyPanel";
import findCurrentContentIdTreeRoute from "@/bundles/utils/findCurrentContentIdTreeRoute";
import { useBundleDataContext } from "@/bundles/providers/useBundleDataContext";
import { useAttachmentsContext } from "@/bundles/providers/useAttachmentsContext";
import {
  footerCss,
  mainContentWrapperCss,
  mainContainerCss,
  centerContentPaneCss,
  outlineToggleButtonCss,
  outlineToggleLabelCss,
  chevronIconCss,
  outlineContainerCss,
  resourceRegionCss,
  resourceContainerCss,
  viewResourcesIconCss,
  resourceTabButtonCss,
  resourceTabInnerContentCss,
} from "./BundlePageContainer.styles";

/** URL query parameter to track the nested Bundle content shown on the
screen. */
const TREE_QUERY_PARAM = "tree";
const ButtonWithTrackEvent = withTrackEvent(Button);

/**
 * Returns a href Nextjs object format with a potentially new `tree` URL query
 * parameter value.
 */
const makeHrefWithTree = (router: NextRouter, tree: string) => ({
  pathname: router.pathname,
  query: { ...router.query, [TREE_QUERY_PARAM]: tree },
});

/**
 * Mutates Nextjs router with a `tree` URL query parameter. If `tree` doesn't
 * represent a new value, nothing happens.
 */
const updateTreeInURL = (router: NextRouter, tree: string) => {
  const currentTree = router.query[TREE_QUERY_PARAM];
  if (currentTree === tree) return;
  router.push(makeHrefWithTree(router, tree), undefined, { shallow: true });
};

const BundlePageContainer = () => {
  const {
    contentOutline,
    currentContentId,
    currentContentIdTreeRouteOrDefault,
    outlineIsLoading,
  } = useBundleDataContext();

  const { hasNoAttachments, isResourcePanelOpen, setIsResourcePanelOpen } =
    useAttachmentsContext();

  const [isOutlineOpen, setIsOutlineOpen] = useState(false);

  const treeItems = contentOutline?.items ?? {};
  const treeRootTitle = useMemo(() => {
    const rootItem = treeItems[contentOutline?.root_item_id as string];

    if (!rootItem) {
      return "Outline";
    } else {
      return rootItem?.name || "";
    }
  }, [contentOutline]);

  // `currentContentIdTree` should always represent a tree of the currently
  // selected content; only the root content ID should have a tree with no
  // slashes `/`. `currentContentIdTreeRouteOrDefault` can be a tree or only
  // the ID of the currently selected content.
  const currentContentIdTree = useMemo(
    () =>
      currentContentIdTreeRouteOrDefault.includes("/")
        ? currentContentIdTreeRouteOrDefault
        : findCurrentContentIdTreeRoute(
            treeItems,
            currentContentIdTreeRouteOrDefault,
          ),
    [treeItems, currentContentIdTreeRouteOrDefault],
  );

  if (outlineIsLoading) {
    return <LoadingSpinner />;
  }

  if (!contentOutline) {
    return <div>This content is not available or doesn't exist.</div>;
  }

  return (
    <div css={mainContainerCss(isResourcePanelOpen)}>
      <section>
        {/* Small screens: sticky toggle button */}
        <ButtonWithTrackEvent
          __cssFor={{ root: outlineToggleButtonCss }}
          flavor={Button.flavors.incognito}
          onClick={() => setIsOutlineOpen((isOpen) => !isOpen)}
          actionName={actionNames.interact}
          objectType={objectTypes.content}
          streamProperties={{
            description:
              "Open bundle page outline tree on tablet/mobile screens",
          }}
        >
          <span css={outlineToggleLabelCss}>{treeRootTitle}</span>
          <ChevronUpSVG css={chevronIconCss(isOutlineOpen)} />
        </ButtonWithTrackEvent>

        {/* Collapsible container on < md, static column on ≥ md */}
        <div css={outlineContainerCss(isOutlineOpen)}>
          <GeneaologyPanel
            contentOutline={contentOutline}
            currentContentIdTree={currentContentIdTree}
            updateTreeInURL={updateTreeInURL}
          />
        </div>
      </section>

      <div css={centerContentPaneCss}>
        <div css={mainContentWrapperCss}>
          <Breadcrumbs
            contentOutline={contentOutline}
            currentContentIdTree={currentContentIdTree}
            makeHrefWithTree={makeHrefWithTree}
          />
          <MainContent currentContentId={currentContentId} />
        </div>
        <Footer customCss={footerCss} />
      </div>
      <div css={resourceRegionCss}>
        {/* Bottom sticky tab (md and down) */}
        {!hasNoAttachments && (
          <ButtonWithTrackEvent
            __cssFor={{ root: resourceTabButtonCss }}
            onClick={() => setIsResourcePanelOpen(true)}
            aria-expanded={isResourcePanelOpen}
            flavor={Button.flavors.incognito}
            actionName={actionNames.interact}
            objectType={objectTypes.content}
            disabled={isResourcePanelOpen}
            streamProperties={{
              description:
                "Open bundle page resource panel on tablet/mobile screens",
            }}
          >
            <div css={resourceTabInnerContentCss}>
              <span>View Resources</span>
              <ChevronUpSVG css={viewResourcesIconCss} />
            </div>
          </ButtonWithTrackEvent>
        )}
        {/* Drawer on < md, right-hand sidebar on ≥ md */}
        {isResourcePanelOpen && (
          <section css={resourceContainerCss(isResourcePanelOpen)}>
            <AttachmentPanel key={currentContentId} />
          </section>
        )}
      </div>
    </div>
  );
};

export default BundlePageContainer;
